import React, { useEffect, useRef, useState } from 'react'
import Constants from '../../../app/utils/constants'

const headingGradientStyle = {
  background: 'linear-gradient(92.21deg, #EA892A 0%, #E35384 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}

const numberBehindTitleClass =
  'pointer-events-none absolute inset-x-0 -top-3 md:-top-5 lg:top-0 font-gilroy-bold leading-none text-purple/35 text-6xl md:text-7xl lg:text-6xl xl:text-6xl 2xl:text-8xl'

const titleOnTopClass =
  'relative z-10 pt-4 md:pt-6 lg:pt-9 xl:pt-9 2xl:pt-14 font-gilroy-bold leading-tight text-xl md:text-xl lg:text-2xl xl:text-2xl 2xl:text-4xl'

const getTwoLineTitle = (title: string) => {
  const words = title.trim().split(/\s+/)

  if (words.length <= 1) return [title, '']

  const splitAt = Math.ceil(words.length / 2)

  return [
    words.slice(0, splitAt).join(' '),
    words.slice(splitAt).join(' '),
  ]
}

const BuildFintech = () => {
  const sectionRef = useRef<HTMLElement | null>(null)

  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            setIsVisible(true)
            obs.disconnect()
          }
        })
      },
      {
        threshold: 0.5,
        rootMargin: '0px 0px -20% 0px',
      }
    )

    if (sectionRef.current) observer.observe(sectionRef.current)

    return () => observer.disconnect()
  }, [])

  const {
    intro,
    introHighlight,
    heading,
    fintechstep,
    items,
  } = Constants.buildFintechSection

  const [leftTop, leftBottom, rightTop, rightBottom, centerBottom] = items

  const [leftTopLine1, leftTopLine2] = getTwoLineTitle(leftTop.title)

  const [leftBottomLine1, leftBottomLine2] =
    getTwoLineTitle(leftBottom.title)

  const [rightTopLine1, rightTopLine2] =
    getTwoLineTitle(rightTop.title)

  const [rightBottomLine1, rightBottomLine2] =
    getTwoLineTitle(rightBottom.title)

  const cardClassName =
    'w-full max-w-[340px] rounded-2xl p-4 backdrop-blur-sm md:max-w-[360px] lg:max-w-[60%] xl:max-w-[60%] 2xl:w-[63%]'

const centerCardClassName =
  'w-full max-w-[320px] rounded-2xl p-4 text-center backdrop-blur-sm md:max-w-[500px] lg:max-w-[560px] xl:max-w-[760px] 2xl:max-w-[920px]'

  const animDuration = '1.2s'

  const leftTopDelay = '0s'
  const leftBottomDelay = '0.4s'
  const rightTopDelay = '0.9s'
  const rightBottomDelay = '1.4s'
  const centerDelay = '2.6s'

  const getStyle = (
    direction: 'left' | 'right' | 'up',
    delay: string
  ): React.CSSProperties => {
    const baseTransform =
      direction === 'left'
        ? 'translateX(-60px)'
        : direction === 'right'
          ? 'translateX(60px)'
          : 'translateY(60px)'

    const animationName =
      direction === 'up'
        ? 'slideUp'
        : direction === 'left'
          ? 'slideInLeft'
          : 'slideInRight'

    return {
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'none' : baseTransform,
      animation: isVisible
        ? `${animationName} ${animDuration} cubic-bezier(.2,.9,.2,1) both`
        : undefined,
      animationDelay: isVisible ? delay : undefined,
      willChange: 'transform, opacity',
    }
  }

  return (
    <section
      ref={(el) => {
        sectionRef.current = el
      }}
      className="py-7 text-white md:py-8 lg:py-10"
    >
      <style>{`
        @keyframes slideInLeft {
          0% {
            opacity: 0;
            transform: translateX(-60px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInRight {
          0% {
            opacity: 0;
            transform: translateX(60px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideUp {
          0% {
            opacity: 0;
            transform: translateY(60px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      <div className="mx-auto w-full">

        {/* Intro */}
        <p className="mx-auto max-w-[95%] text-center font-gilroy-medium text-base leading-relaxed text-white md:max-w-[90%] md:text-lg lg:max-w-[85%] lg:text-xl xl:max-w-[70%] xl:text-2xl 2xl:text-3xl">
          {intro.split(introHighlight)[0]}
          <span className="text-purple">{introHighlight}</span>
          {intro.split(introHighlight)[1]}
        </p>

        {/* Heading */}
        <div className="mx-auto mt-10 flex max-w-[90%] items-center justify-center gap-4 md:max-w-[70%] md:gap-6">
          <div className="hidden md:block h-px min-w-8 flex-1 bg-purple md:min-w-16" />

          <h2 className="shrink-0 text-center font-rajdhani text-3xl font-semibold text-white md:text-4xl xl:text-5xl 2xl:text-6xl">
            {heading}
          </h2>

          <div className="hidden md:block h-px min-w-8 flex-1 bg-purple md:min-w-16" />
        </div>

        {/* Main Section */}
        <div className="relative mx-auto min-h-[1250px] rounded-[32px] py-6 md:min-h-[1100px] md:py-10 lg:mt-12 lg:min-h-[760px] lg:max-w-[95%] lg:py-10 xl:min-h-[880px] xl:max-w-[90%] 2xl:min-h-[1000px] 2xl:max-w-[90%]">

          {/* Center Image */}
          <div className="mx-auto flex w-full justify-center lg:absolute lg:left-1/2 lg:top-0 lg:-translate-x-1/2">
            <img
              src={fintechstep}
              alt="Build Fintech"
              className="h-auto w-[85%] object-contain md:w-[420px] lg:mt-[160px] lg:w-[400px] xl:mt-[65px] xl:w-[480px] 2xl:mt-[50px] 2xl:w-[640px]"
            />
          </div>

          {/* MOBILE VIEW */}
          <div className="mt-10 flex flex-col items-center gap-6 md:hidden">
            {/* Items 1–4 */}
            {[leftTop, leftBottom, rightTop, rightBottom].map((item) => {
              const [line1, line2] = getTwoLineTitle(item.title)

              return (
                <article
                  key={item.number}
                  className="w-full max-w-[340px] rounded-2xl p-4 text-center backdrop-blur-sm"
                >
                  <div className="relative min-h-[54px]">
                    <h4 className={numberBehindTitleClass}>
                      {item.number}
                    </h4>

                    <h5
                      className={`${titleOnTopClass} text-center`}
                      style={headingGradientStyle}
                    >
                      <span className="block">{line1}</span>
                      <span className="block">
                        {line2 || '\u00A0'}
                      </span>
                    </h5>
                  </div>

                  <p className="mt-2 text-sm text-gray-300">
                    {item.description}
                  </p>
                </article>
              )
            })}

            {/* Item 5 — centerBottom */}
            <article className={`${centerCardClassName} text-center`}>
              <div className="relative min-h-[54px]">
                <h4 className={`${numberBehindTitleClass} text-4xl`}>
                  {centerBottom.number}
                </h4>

                <h5
                  className={`${titleOnTopClass} text-center`}
                  style={headingGradientStyle}
                >
                  {centerBottom.title}
                </h5>
              </div>

              <p className="mt-2 text-sm text-gray-300">
                {centerBottom.description}
              </p>
            </article>
          </div>

          {/* TABLET VIEW */}
          <div className="mt-10 hidden md:block lg:hidden">
            <div className="grid grid-cols-2 gap-y-10">

              {/* Row 1 */}
              <div className="flex justify-start ml-5">
                <article
                  className={`${cardClassName} text-start`}
                >
                  <div className="relative min-h-[64px]">
                    <h4 className={numberBehindTitleClass}>
                      {leftTop.number}
                    </h4>

                    <h5
                      className={titleOnTopClass}
                      style={headingGradientStyle}
                    >
                      <span className="block">
                        {leftTopLine1}
                      </span>

                      <span className="block">
                        {leftTopLine2 || '\u00A0'}
                      </span>
                    </h5>
                  </div>

                  <p className="mt-2 text-base text-gray-300">
                    {leftTop.description}
                  </p>
                </article>
              </div>

              <div className="flex justify-end mr-5">
                <article
                  className={`${cardClassName} text-end`}
                >
                  <div className="relative min-h-[64px]">
                    <h4 className={numberBehindTitleClass}>
                      {rightTop.number}
                    </h4>

                    <h5
                      className={titleOnTopClass}
                      style={headingGradientStyle}
                    >
                      <span className="block">
                        {rightTopLine1}
                      </span>

                      <span className="block">
                        {rightTopLine2 || '\u00A0'}
                      </span>
                    </h5>
                  </div>

                  <p className="mt-2 text-base text-gray-300">
                    {rightTop.description}
                  </p>
                </article>
              </div>

              {/* Row 2 */}
              <div className="flex justify-start ml-5">
                <article
                  className={`${cardClassName} text-start`}
                >
                  <div className="relative min-h-[64px]">
                    <h4 className={numberBehindTitleClass}>
                      {leftBottom.number}
                    </h4>

                    <h5
                      className={titleOnTopClass}
                      style={headingGradientStyle}
                    >
                      <span className="block">
                        {leftBottomLine1}
                      </span>

                      <span className="block">
                        {leftBottomLine2 || '\u00A0'}
                      </span>
                    </h5>
                  </div>

                  <p className="mt-2 text-base text-gray-300">
                    {leftBottom.description}
                  </p>
                </article>
              </div>

              <div className="flex justify-end mr-5">
                <article
                  className={`${cardClassName} text-end`}
                >
                  <div className="relative min-h-[64px]">
                    <h4 className={numberBehindTitleClass}>
                      {rightBottom.number}
                    </h4>

                    <h5
                      className={titleOnTopClass}
                      style={headingGradientStyle}
                    >
                      <span className="block">
                        {rightBottomLine1}
                      </span>

                      <span className="block">
                        {rightBottomLine2 || '\u00A0'}
                      </span>
                    </h5>
                  </div>

                  <p className="mt-2 text-base text-gray-300">
                    {rightBottom.description}
                  </p>
                </article>
              </div>

              {/* Row 3 — centerBottom spanning full width */}
              <div className="col-span-2 flex justify-center px-5">
                <article className={`${centerCardClassName} text-center`}>
                  <div className="relative min-h-[64px]">
                    <h4 className={`${numberBehindTitleClass} text-5xl md:-top-2`}>
                      {centerBottom.number}
                    </h4>

                    <h5
                      className={`${titleOnTopClass} text-center whitespace-normal md:whitespace-nowrap`}
                      style={headingGradientStyle}
                    >
                      {centerBottom.title}
                    </h5>
                  </div>

                  <p className="mt-2 text-base text-gray-300">
                    {centerBottom.description}
                  </p>
                </article>
              </div>

            </div>
          </div>

          {/* DESKTOP VIEW */}
          <div className="mt-10 hidden gap-8 lg:grid lg:grid-cols-2 lg:items-center">

            {/* LEFT */}
            <div className="flex flex-col items-start gap-5 lg:mt-[-80px] xl:mt-[-20px]">

              <article
                className={`${cardClassName} text-start`}
                style={getStyle('left', leftTopDelay)}
              >
                <div className="relative min-h-[76px]">
                  <h4 className={numberBehindTitleClass}>
                    {leftTop.number}
                  </h4>

                  <h5
                    className={titleOnTopClass}
                    style={headingGradientStyle}
                  >
                    <span className="block">
                      {leftTopLine1}
                    </span>

                    <span className="block">
                      {leftTopLine2 || '\u00A0'}
                    </span>
                  </h5>
                </div>

                <p className="mt-2 text-gray-300 xl:text-md 2xl:text-xl">
                  {leftTop.description}
                </p>
              </article>

              <article
                className={`${cardClassName} text-start`}
                style={getStyle('left', leftBottomDelay)}
              >
                <div className="relative min-h-[76px]">
                  <h4 className={numberBehindTitleClass}>
                    {leftBottom.number}
                  </h4>

                  <h5
                    className={titleOnTopClass}
                    style={headingGradientStyle}
                  >
                    <span className="block">
                      {leftBottomLine1}
                    </span>

                    <span className="block">
                      {leftBottomLine2 || '\u00A0'}
                    </span>
                  </h5>
                </div>

                <p className="mt-2 text-gray-300 xl:text-md 2xl:text-xl">
                  {leftBottom.description}
                </p>
              </article>
            </div>

            {/* RIGHT */}
            <div className="flex flex-col items-end gap-5 lg:mt-[-80px] xl:mt-[-20px]">

              <article
                className={`${cardClassName} text-end`}
                style={getStyle('right', rightTopDelay)}
              >
                <div className="relative min-h-[76px]">
                  <h4 className={numberBehindTitleClass}>
                    {rightTop.number}
                  </h4>

                  <h5
                    className={titleOnTopClass}
                    style={headingGradientStyle}
                  >
                    <span className="block">
                      {rightTopLine1}
                    </span>

                    <span className="block">
                      {rightTopLine2 || '\u00A0'}
                    </span>
                  </h5>
                </div>

                <p className="mt-2 text-gray-300 xl:text-md 2xl:text-xl">
                  {rightTop.description}
                </p>
              </article>

              <article
                className={`${cardClassName} text-end`}
                style={getStyle('right', rightBottomDelay)}
              >
                <div className="relative min-h-[76px]">
                  <h4 className={numberBehindTitleClass}>
                    {rightBottom.number}
                  </h4>

                  <h5
                    className={titleOnTopClass}
                    style={headingGradientStyle}
                  >
                    <span className="block">
                      {rightBottomLine1}
                    </span>

                    <span className="block">
                      {rightBottomLine2 || '\u00A0'}
                    </span>
                  </h5>
                </div>

                <p className="mt-2 text-gray-300 xl:text-md 2xl:text-xl">
                  {rightBottom.description}
                </p>
              </article>
            </div>
          </div>

          {/* CENTER CARD — lg and above only (mobile & tablet render it inside their own view blocks) */}
          <div className="hidden lg:flex mt-14 justify-center xl:absolute xl:left-1/2 xl:top-[70%] xl:mt-0 xl:-translate-x-1/2 2xl:top-[75%]">
            <article
              className={`${centerCardClassName} relative z-10 text-center`}
              style={getStyle('up', centerDelay)}
            >
              <div className="relative min-h-[54px] md:min-h-[64px] lg:min-h-[76px] xl:min-h-[88px] 2xl:min-h-[102px]">

                <h4
                  className={`${numberBehindTitleClass} text-4xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl md:-top-2 lg:-top-4`}
                >
                  {centerBottom.number}
                </h4>

                <h5
                  className={`${titleOnTopClass} text-center whitespace-normal md:whitespace-nowrap`}
                  style={headingGradientStyle}
                >
                  {centerBottom.title}
                </h5>
              </div>

              <p className="mt-2 text-sm text-gray-300 md:text-base lg:text-sm xl:text-md 2xl:text-xl">
                {centerBottom.description}
              </p>
            </article>
          </div>
        </div>
      </div>
    </section>
  )
}

export default BuildFintech